import { useEffect } from "react";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import styled from "@emotion/styled";
import { Button, Flex, Table, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  DefaultApiWmsBulkDiligencePutRequest,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  WmsBulkDiligencePutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface DiligenceAllFormProps {
  formatterProps: ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner[];
}

export const DiligenceAllForm = ({ formatterProps }: DiligenceAllFormProps) => {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mapFormatterToFormValue = (
    items: ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner[] = [],
  ) => {
    return items.map((item) => ({
      // 화면 표시용 필드
      itemType: t(item?.itemType as string),
      itemName: item.itemName as string,
      gradeName: item.grade?.name as string,
      itemCategoryName: item.itemCategory?.name as string,
      abstractItemCode: item.abstractItem?.code as string,
      lotName: item.lot?.name as string,
      locationName: item.locationName as string,

      // API 요청에 필요한 필드
      itemCode: item.itemCode as string,
      locationCode: item.locationCode as string,
      quantity: item.quantity as string,
      lotId: item.lotId || 0,
      lotInfo: {
        name: item.lot?.name as string,
        expiration: item.lot?.expiration as string,
        actionFrom: "WEB",
      },
    }));
  };

  const { mutate: diligenceAllMutate } = useMutation(
    (params: DefaultApiWmsBulkDiligencePutRequest) =>
      mutateInventories
        .diligenceAll(params)
        .mutationFn(params as DefaultApiWmsBulkDiligencePutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        closeModal(form.values);
        customAlert("재고 조정에 성공하였습니다.", "재고 조정 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "재고 조정" }),
          "재고 조정 실패",
          "red",
        );
      },
    },
  );

  const form = useForm<Partial<WmsBulkDiligencePutRequest>>({
    initialValues: {
      diligence: mapFormatterToFormValue(formatterProps),
    },
  });

  useEffect(() => {
    form.setValues({ diligence: mapFormatterToFormValue(formatterProps) });
  }, [formatterProps]);

  const onSubmit = () => {
    try {
      diligenceAllMutate({
        wmsBulkDiligencePutRequest: {
          diligence: form.values.diligence ?? [],
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: "재고 조정" }),
        "재고 조정 실패",
        "red",
      );
    }
  };

  return (
    <Flex w="70rem" direction="column" gap="xl">
      <Table>
        <thead>
          <tr>
            <Th width={5.75}>시퀀스</Th>
            <Th width={7.125}>품목군</Th>
            <Th width={11.125}>품목명</Th>
            <Th width={11.125}>Grade</Th>
            <Th width={13.125}>Color</Th>
            <Th width={13.125}>형태</Th>
            <Th width={13.125}>로트명</Th>
            <Th width={13.125}>로케이션명</Th>
            <Th width={7.5}>수량</Th>
          </tr>
        </thead>
        <tbody>
          {form.values.diligence?.map((item, index) => (
            <tr key={item.lotId}>
              <td>{index + 1}</td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.itemType`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.itemName`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.gradeName`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.itemCategoryName`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.abstractItemCode`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.lotName`).value}</Text>
              </td>
              <td>
                <Text>{form.getInputProps(`diligence.${index}.locationName`).value}</Text>
              </td>
              <td>
                <TextInput type="number" {...form.getInputProps(`diligence.${index}.quantity`)} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Flex justify="flex-end" gap="sm">
        <Button color="gray" onClick={() => closeModal({})}>
          취소
        </Button>
        <Button onClick={onSubmit}>재고 일괄 조정</Button>
      </Flex>
    </Flex>
  );
};

const Th = styled.th<{ width?: number }>`
  width: ${(props) => (props.width ? `${props.width}rem` : "auto")};
`;
